import { useState, useEffect } from "react";
import {
  Button,
  Spinner,
  Alert,
  Card,
  Table,
  Row,
  Col,
  Form,
  Badge,
  Container,
  Modal
} from "react-bootstrap";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { PaymentErrorLog, PaginatedResponse } from "../../types/Types";
import { FiRefreshCw, FiFilter, FiEye, FiDownload } from "react-icons/fi";

interface PaymentErrorsResponse extends PaginatedResponse<PaymentErrorLog> {
  totalCount: number;
}

interface PaymentErrorsSummary {
  totalErrors: number;
  errorsByType: Array<{ errorType: string; count: number }>;
  errorsByStripeCode: Array<{ stripeErrorCode: string; count: number }>;
  recentErrors: PaymentErrorLog[];
}

const PaymentErrors = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentErrors, setPaymentErrors] = useState<PaymentErrorLog[]>([]);
  const [summary, setSummary] = useState<PaymentErrorsSummary | null>(null);
  const [selectedError, setSelectedError] = useState<PaymentErrorLog | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    userId: "",
  });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0,
  });

  useEffect(() => {
    loadPaymentErrors();
    loadSummary();
  }, [pagination.pageNumber, filters]);

  const loadPaymentErrors = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) {
        params.append("fromDate", new Date(filters.fromDate).toISOString());
      }
      if (filters.toDate) {
        params.append("toDate", new Date(filters.toDate).toISOString());
      }
      if (filters.userId) {
        params.append("userId", filters.userId);
      }
      params.append("pageNumber", pagination.pageNumber.toString());
      params.append("pageSize", pagination.pageSize.toString());

      const url = getAPIURL(`/PaymentErrorLog?${params.toString()}`);
      const res = await axiosInstance.get<PaymentErrorsResponse>(url);
      setPaymentErrors(res.data.data);
      setPagination({
        ...pagination,
        totalCount: res.data.totalCount,
        totalPages: res.data.totalPages,
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load payment errors");
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) {
        params.append("fromDate", new Date(filters.fromDate).toISOString());
      }
      if (filters.toDate) {
        params.append("toDate", new Date(filters.toDate).toISOString());
      }

      const url = getAPIURL(`/PaymentErrorLog/summary?${params.toString()}`);
      const res = await axiosInstance.get<PaymentErrorsSummary>(url);
      setSummary(res.data);
    } catch (err: any) {
      console.error("Failed to load summary:", err);
    }
  };

  const handleViewDetails = (errorLog: PaymentErrorLog) => {
    setSelectedError(errorLog);
    setShowDetailsModal(true);
  };

  const handleResetFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      userId: "",
    });
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const handleExport = () => {
    const csv = buildCsv(paymentErrors);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-errors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildCsv = (errors: PaymentErrorLog[]): string => {
    const headers = [
      "Id",
      "UserId",
      "UserEmail",
      "UserName",
      "ProductId",
      "PaymentMethodId",
      "CustomerId",
      "ErrorType",
      "ErrorMessage",
      "StripeErrorCode",
      "StripeErrorType",
      "IsEvent",
      "IsAnnual",
      "DiscountCode",
      "ErrorOccurredAt",
    ];

    const rows = errors.map((err) => [
      err.id.toString(),
      err.userId,
      err.userEmail || "",
      err.userName || "",
      err.productId || "",
      err.paymentMethodId || "",
      err.customerId || "",
      err.errorType || "",
      err.errorMessage,
      err.stripeErrorCode || "",
      err.stripeErrorType || "",
      err.isEvent ? "Yes" : "No",
      err.isAnnual ? "Yes" : "No",
      err.discountCode || "",
      new Date(err.errorOccurredAt).toLocaleString(),
    ]);

    return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
  };

  const getErrorBadgeVariant = (errorType?: string): string => {
    switch (errorType) {
      case "StripeException":
        return "danger";
      case "ValidationError":
        return "warning";
      case "BusinessValidationError":
        return "warning";
      case "SystemException":
        return "danger";
      case "StripeCustomerMissing":
        return "danger";
      case "UserMissingInDb":
        return "warning";
      case "MembershipMissingInDb":
        return "warning";
      case "ClientError":
        return "info";
      default:
        return "secondary";
    }
  };

  return (
    <Container fluid className="mt-3">
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="bg-project-gradient">
          <h4 className="mb-0">
            <FiRefreshCw className="me-2" />
            Payment Error Logs
          </h4>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            View and analyze payment processing failures. Check logs to identify issues affecting users.
          </p>

          {/* Summary Cards */}
          {summary && (
            <Row className="mb-4">
              <Col md={3}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body className="text-center">
                    <Card.Title className="text-muted small mb-2">
                      Total Errors
                    </Card.Title>
                    <h2 className="text-danger mb-0">{summary.totalErrors}</h2>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={9}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <Card.Title className="text-muted small mb-2">
                      Errors by Type
                    </Card.Title>
                    <div className="d-flex flex-wrap gap-2">
                      {summary.errorsByType.map((item, idx) => (
                        <Badge key={idx} bg={getErrorBadgeVariant(item.errorType)}>
                          {item.errorType}: {item.count}
                        </Badge>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Filters */}
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">
                <FiFilter className="me-2" />
                Filters
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.fromDate}
                      onChange={(e) =>
                        setFilters({ ...filters, fromDate: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.toDate}
                      onChange={(e) =>
                        setFilters({ ...filters, toDate: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>User ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Filter by User ID"
                      value={filters.userId}
                      onChange={(e) =>
                        setFilters({ ...filters, userId: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      onClick={loadPaymentErrors}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <FiRefreshCw className="me-2" />
                          Refresh
                        </>
                      )}
                    </Button>
                    <Button variant="outline-secondary" onClick={handleResetFilters}>
                      Reset Filters
                    </Button>
                    <Button variant="success" onClick={handleExport}>
                      <FiDownload className="me-2" />
                      Export CSV
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Error Messages */}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {/* Payment Errors Table */}
          <Card>
            <Card.Header className="bg-light">
              <h5 className="mb-0">Payment Errors</h5>
              <small className="text-muted">
                Showing {paymentErrors.length} of {pagination.totalCount} errors
              </small>
            </Card.Header>
            <Card.Body>
              {loading && paymentErrors.length === 0 ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                  <p className="mt-3 text-muted">Loading payment errors...</p>
                </div>
              ) : paymentErrors.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No payment errors found.</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>User</th>
                          <th>Error Type</th>
                          <th>Error Message</th>
                          <th>Stripe Code</th>
                          <th>Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentErrors.map((err) => (
                          <tr key={err.id}>
                            <td>{err.id}</td>
                            <td>
                              <div>
                                <div>{err.userName || err.userEmail || err.userId}</div>
                                <small className="text-muted">{err.userEmail}</small>
                              </div>
                            </td>
                            <td>
                              <Badge bg={getErrorBadgeVariant(err.errorType)}>
                                {err.errorType || "Unknown"}
                              </Badge>
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: "300px" }}>
                                {err.errorMessage}
                              </div>
                            </td>
                            <td>
                              {err.stripeErrorCode ? (
                                <Badge bg="info">{err.stripeErrorCode}</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {new Date(err.errorOccurredAt).toLocaleString()}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewDetails(err)}
                              >
                                <FiEye className="me-1" />
                                Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Page {pagination.pageNumber} of {pagination.totalPages}
                      </div>
                      <div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={pagination.pageNumber === 1}
                          onClick={() =>
                            setPagination({ ...pagination, pageNumber: pagination.pageNumber - 1 })
                          }
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="ms-2"
                          disabled={pagination.pageNumber === pagination.totalPages}
                          onClick={() =>
                            setPagination({ ...pagination, pageNumber: pagination.pageNumber + 1 })
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Payment Error Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedError && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Error ID:</strong> {selectedError.id}
                </Col>
                <Col md={6}>
                  <strong>Date:</strong>{" "}
                  {new Date(selectedError.errorOccurredAt).toLocaleString()}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>User ID:</strong> {selectedError.userId}
                </Col>
                <Col md={6}>
                  <strong>User Email:</strong> {selectedError.userEmail || "N/A"}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>User Name:</strong> {selectedError.userName || "N/A"}
                </Col>
                <Col md={6}>
                  <strong>Customer ID:</strong> {selectedError.customerId || "N/A"}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Product ID:</strong> {selectedError.productId || "N/A"}
                </Col>
                <Col md={6}>
                  <strong>Payment Method ID:</strong>{" "}
                  {selectedError.paymentMethodId || "N/A"}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Error Type:</strong>{" "}
                  <Badge bg={getErrorBadgeVariant(selectedError.errorType)}>
                    {selectedError.errorType || "Unknown"}
                  </Badge>
                </Col>
                <Col md={6}>
                  <strong>Stripe Error Code:</strong>{" "}
                  {selectedError.stripeErrorCode || "N/A"}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <strong>Error Message:</strong>
                  <div className="alert alert-danger mt-2">{selectedError.errorMessage}</div>
                </Col>
              </Row>
              {selectedError.stripeErrorType && (
                <Row className="mb-3">
                  <Col>
                    <strong>Stripe Error Type:</strong> {selectedError.stripeErrorType}
                  </Col>
                </Row>
              )}
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Is Event:</strong> {selectedError.isEvent ? "Yes" : "No"}
                </Col>
                <Col md={6}>
                  <strong>Is Annual:</strong> {selectedError.isAnnual ? "Yes" : "No"}
                </Col>
              </Row>
              {selectedError.discountCode && (
                <Row className="mb-3">
                  <Col>
                    <strong>Discount Code:</strong> {selectedError.discountCode}
                  </Col>
                </Row>
              )}
              {selectedError.requestDetails && (
                <Row className="mb-3">
                  <Col>
                    <strong>Request Details:</strong>
                    <pre className="bg-light p-2 mt-2 rounded">
                      {(() => {
                        try {
                          const parsed = JSON.parse(selectedError.requestDetails);
                          return JSON.stringify(parsed, null, 2);
                        } catch {
                          return selectedError.requestDetails;
                        }
                      })()}
                    </pre>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PaymentErrors;
